import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import Button from '@ott/button';
import FirstErrorShower, { prepareRHFErrorsDeeply } from '@ott/first-error-shower';
import Input from '@ott/input';
import { l10n } from '@ott/l10n';
import LoadingOverlay from '@ott/loading-overlay';
import { useEnterSubmit } from '@ott/utility-enter-submit';

import { yupResolver } from '@hookform/resolvers/yup';

import { forgotPasswordScheme } from '../../helpers/validationSchema';
import { forgotPassword, regReset } from '../../redux/auth/actions';
import { authSelector } from '../../redux/auth/selectors';
import { AuthDispatch } from '../../redux/auth/types';
import { FORM_FIELDS } from './helpers/constants';
import { FormData } from './types';

import styles from './ForgotPasswordForm.scss';

const componentName = 'ForgotPasswordForm';

type Props = {
  reqHost: string;
  onClose: VoidFunction;
};

const ForgotPasswordForm = ({ reqHost, onClose }: Props) => {
  const dispatch = useDispatch<AuthDispatch>();
  const [submitTime, setSubmitTime] = useState<number | null>(null);
  const { formRef, submitRef } = useEnterSubmit();
  const { forgotStatus } = useSelector(authSelector);

  const form = useForm<FormData>({
    shouldFocusError: true,
    resolver: yupResolver(forgotPasswordScheme),
  });

  const { errors, isSubmitSuccessful, isSubmitted } = form.formState;

  useEffect(
    () => () => {
      dispatch(regReset());
    },
    []
  );

  const submit = async ({ passwordRecoveryEmail: email }: FormData) => {
    setSubmitTime(Date.now());

    dispatch(forgotPassword({ email, reqHost }));
  };

  if (forgotStatus.success) {
    return (
      <div className={styles.ForgotPasswordForm} data-locator={componentName}>
        <div className={styles.confirmPassword}>
          {l10n('auth.newPassword', { email: form.watch(FORM_FIELDS.passwordRecoveryEmail) })}
        </div>
        <div className={styles.letterSpam}>{l10n('auth.letterSpam')}</div>
        <Button
          className={styles.button}
          onClick={onClose}
          specificLocators={[`${componentName}--close`]}
          withFullWidth={true}
        >
          {l10n('auth.close')}
        </Button>
      </div>
    );
  }

  const preparedErrors = prepareRHFErrorsDeeply(errors);

  return (
    <div className={styles.ForgotPasswordForm} data-locator={componentName}>
      <form onSubmit={form.handleSubmit(submit)} ref={formRef}>
        <FirstErrorShower
          errors={preparedErrors}
          show={!isSubmitSuccessful && isSubmitted}
          useRHFErrors={true}
          time={submitTime}
          container={formRef.current}
          overlayContainer={formRef.current}
        />
        <div className={styles.text}>{l10n('auth.registrationEmail')}</div>
        {forgotStatus.error && <div className={styles.error}>{l10n('auth.notExist')}</div>}
        <Controller
          control={form.control}
          name={FORM_FIELDS.passwordRecoveryEmail}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              label={l10n('auth.email')}
              hasError={(isSubmitted || fieldState.isTouched) && fieldState.invalid}
              autoComplete="on"
              autoFocus={true}
              specificLocators={`${componentName}--email`}
            />
          )}
        />
        {forgotStatus.loading && <LoadingOverlay />}
        <Button
          innerRef={submitRef}
          className={styles.button}
          type="submit"
          isDisabled={forgotStatus.loading}
          withFullWidth={true}
          specificLocators={[`${componentName}--submit`]}
        >
          {l10n('auth.restorePassword')}
        </Button>
      </form>
    </div>
  );
};

export default ForgotPasswordForm;
